/**
 * API audit crédits — verify que chaque (option × niveau × semestre) totalise 30.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export interface AuditGroupe {
  option: string;
  pole?: string;   // Fix 2026-09-08 : pôle pour filtrage
  niveau: string;
  semestre: number;
  total_credits: number;
  nb_matieres: number;
  nb_ues: number;
  ecart: { valeur: number; statut: 'ok' | 'excedent' | 'deficit'; label: string };
}

export interface AuditOrpheline {
  id: number; code: string; nom: string;
  niveau: string; semestre: number; credits: number;
}

export interface AuditOverviewResponse {
  cible: number;
  results: AuditGroupe[];
  total_groupes: number;
  kpis: { conformes: number; excedent: number; deficit: number };
  orphelines: AuditOrpheline[];
  nb_orphelines: number;
}

export interface AuditMatiereDetail {
  id: number;
  code_matiere: string;
  nom_matiere: string;
  credits: number;
  coefficient_devoir: number;
  coefficient_examen: number;
  nb_notes_devoir: number;
  nb_notes_examen: number;
  a_des_notes: boolean;
}

export interface AuditUEDetail {
  ue_id: number;
  ue_code: string;
  ue_nom: string;
  sous_total: number;
  matieres: AuditMatiereDetail[];
}

export interface AuditDetailResponse {
  option: string;
  niveau: string;
  semestre: number;
  total_credits: number;
  cible: number;
  ecart: { valeur: number; statut: 'ok' | 'excedent' | 'deficit'; label: string };
  ues: AuditUEDetail[];
  nb_matieres: number;
}

export interface AuditModification extends AuditMatiereDetail {
  credits_avant: number;
  credits_apres: number;
  delta: number;
}

export interface AuditDistribuerResponse {
  detail: AuditDetailResponse;
  modifications: AuditModification[];
  total_simule: number;
  ecart_simule: AuditGroupe['ecart'];
  ecart_resorbe: boolean;
  message: string;
}

export interface AuditSaveResponse {
  success: boolean;
  detail_apres: AuditDetailResponse;
  backup_path: string | null;
  nb_modifiees: number;
  force: boolean;
  raison: string | null;
}

export const auditCreditsApi = createApi({
  reducerPath: 'auditCreditsApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['AuditOverview', 'AuditDetail'],
  endpoints: (builder) => ({
    auditOverview: builder.query<AuditOverviewResponse, {
      niveau?: string; semestre?: number; type_formation?: 'FI' | 'CS';
    } | void>({
      query: (params) => ({ url: '/audit-credits/', params: params ?? undefined }),
      providesTags: ['AuditOverview'],
    }),

    auditDetail: builder.query<AuditDetailResponse, {
      option: string; niveau: string; semestre: number;
    }>({
      query: (params) => ({ url: '/audit-credits/detail/', params }),
      providesTags: ['AuditDetail'],
    }),

    auditDistribuer: builder.mutation<AuditDistribuerResponse, {
      option: string; niveau: string; semestre: number;
      exclure_matieres_avec_notes?: boolean;
    }>({
      query: (body) => ({
        url: '/audit-credits/distribuer/', method: 'POST', body,
      }),
    }),

    auditSave: builder.mutation<AuditSaveResponse, {
      option: string; niveau: string; semestre: number;
      modifications: { id: number; credits: number }[];
      force?: boolean; raison?: string;
    }>({
      query: (body) => ({
        url: '/audit-credits/save/', method: 'POST', body,
      }),
      invalidatesTags: ['AuditOverview', 'AuditDetail'],
    }),

    // Fix 2026-09-08 : sauvegarde multi-options (par nom de matière)
    auditSaveMulti: builder.mutation<AuditSaveMultiResponse, {
      options: string[]; niveau: string; semestre: number;
      modifications_par_nom: { nom_matiere: string; credits: number }[];
      force?: boolean; raison?: string; skip_recalc?: boolean;
    }>({
      query: (body) => ({
        url: '/audit-credits/save-multi/', method: 'POST', body,
      }),
      invalidatesTags: ['AuditOverview', 'AuditDetail'],
    }),
  }),
});

export interface AuditSaveMultiResponse {
  success: boolean;
  niveau: string;
  semestre: number;
  options: string[];
  total_modifiees: number;
  resultats: Array<{
    option: string;
    nb_modifiees: number;
    total_apres: number;
    modifications?: Array<{
      id: number; code: string; nom: string;
      credits_avant: number; credits_apres: number;
    }>;
    skipped: boolean;
    motif?: string;
  }>;
  force: boolean;
  raison: string | null;
  recalcul: {
    effectue: boolean;
    etudiants_recalcules: number;
    erreurs: number;
    duree_secondes: number;
  } | null;
}

export const {
  useAuditOverviewQuery,
  useAuditDetailQuery,
  useAuditDistribuerMutation,
  useAuditSaveMutation,
  useAuditSaveMultiMutation,
} = auditCreditsApi;
